/**
 * SubscriptionPaywall.js
 *
 * Full-screen paywall overlay for the institution-pays model.
 * Displays when the institute's subscription is inactive/expired.
 * Shows the current tier and directs users to contact their admin.
 */

import React from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase-config';
import { SecureLog, SubscriptionEvents } from '../utils/SecurityLogger';

// --- THEME (matches existing dashboard) ---
const THEME = {
    colors: {
        midnightBlue: '#0F172A',
        midnightSurface: '#1E293B',
        goldAccent: '#F59E0B',
        tealAccent: '#14B8A6',
        successColor: '#22C55E',
        errorColor: '#EF4444',
        textPrimary: '#F8FAFC',
        textSecondary: '#94A3B8',
        borderColor: '#334155',
        dividerColor: '#334155',
    },
    fonts: {
        heading: "'Poppins', sans-serif",
        body: "'Inter', sans-serif",
    }
};

// ========== ANIMATED BACKGROUND ==========
const AnimatedBackground = () => (
    <div style={{
        position: 'fixed',
        inset: 0,
        background: `linear-gradient(135deg, ${THEME.colors.midnightBlue} 0%, #0C1220 50%, ${THEME.colors.midnightBlue} 100%)`,
        overflow: 'hidden',
        zIndex: 0,
    }}>
        {[
            { size: 300, x: '10%', y: '20%', color: THEME.colors.goldAccent, delay: '0s', duration: '20s' },
            { size: 250, x: '80%', y: '60%', color: THEME.colors.tealAccent, delay: '5s', duration: '25s' },
            { size: 200, x: '50%', y: '80%', color: '#8B5CF6', delay: '10s', duration: '22s' },
        ].map((orb, i) => (
            <div key={i} style={{
                position: 'absolute',
                width: orb.size,
                height: orb.size,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${orb.color}15 0%, transparent 70%)`,
                left: orb.x,
                top: orb.y,
                transform: 'translate(-50%, -50%)',
                animation: `float-orb-${i} ${orb.duration} ease-in-out infinite`,
                animationDelay: orb.delay,
            }} />
        ))}
        <style>{`
            @keyframes float-orb-0 { 0%, 100% { transform: translate(-50%, -50%) scale(1); } 50% { transform: translate(-40%, -60%) scale(1.2); } }
            @keyframes float-orb-1 { 0%, 100% { transform: translate(-50%, -50%) scale(1); } 50% { transform: translate(-60%, -40%) scale(1.15); } }
            @keyframes float-orb-2 { 0%, 100% { transform: translate(-50%, -50%) scale(1); } 50% { transform: translate(-45%, -55%) scale(1.1); } }
        `}</style>
    </div>
);

// ========== TIER DISPLAY CONFIG ==========
const TIER_LABELS = {
    free: 'Free',
    starter: 'Starter',
    pro: 'Pro',
    enterprise: 'Enterprise',
};

// ========== MAIN PAYWALL COMPONENT ==========
export default function SubscriptionPaywall({ userData, onLogout, onSubscriptionActivated, tierName }) {
    const displayTier = TIER_LABELS[tierName] || TIER_LABELS.free;

    SubscriptionEvents.expired();

    const handleLogout = async () => {
        try {
            await signOut(auth);
            if (onLogout) onLogout();
        } catch (err) {
            SecureLog.error('Sign out error:', err);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflow: 'auto',
        }}>
            <AnimatedBackground />

            {/* Logout Button (Top Right) */}
            <div style={{
                position: 'fixed',
                top: 'max(16px, env(safe-area-inset-top))',
                right: 16,
                zIndex: 1010,
            }}>
                <button
                    onClick={handleLogout}
                    style={{
                        background: 'rgba(30, 41, 59, 0.6)',
                        border: 'none',
                        borderRadius: '50%',
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: THEME.colors.textPrimary,
                        transition: 'background 0.2s',
                    }}
                    title="Sign Out"
                    onMouseEnter={e => e.target.style.background = 'rgba(30, 41, 59, 0.9)'}
                    onMouseLeave={e => e.target.style.background = 'rgba(30, 41, 59, 0.6)'}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                </button>
            </div>

            {/* Content */}
            <div style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '60px 24px 24px',
                maxWidth: 440,
                width: '100%',
            }}>
                {/* Hero: Diamond Icon with Pulsing Glow */}
                <div style={{ position: 'relative', marginBottom: 32 }}>
                    <div style={{
                        position: 'absolute',
                        inset: -30,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${THEME.colors.goldAccent}30 0%, transparent 70%)`,
                        animation: 'glow-pulse 2s ease-in-out infinite',
                    }} />
                    <div style={{
                        position: 'relative',
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: THEME.colors.midnightSurface,
                        border: `1px solid ${THEME.colors.goldAccent}4D`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'icon-pulse 1.5s ease-in-out infinite',
                    }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={THEME.colors.goldAccent} strokeWidth="1.5">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                        </svg>
                    </div>
                    <div style={{
                        position: 'absolute',
                        bottom: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: THEME.colors.errorColor,
                        borderRadius: 8,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#fff',
                        letterSpacing: 0.5,
                        fontFamily: THEME.fonts.body,
                    }}>
                        INACTIVE
                    </div>
                </div>

                {/* Text Content */}
                <h1 style={{
                    color: THEME.colors.textPrimary,
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: 'center',
                    margin: '0 0 12px',
                    fontFamily: THEME.fonts.heading,
                }}>
                    Subscription Inactive
                </h1>

                {/* Tier Chip */}
                <div style={{
                    background: 'rgba(99, 102, 241, 0.15)',
                    border: '1px solid rgba(99, 102, 241, 0.4)',
                    borderRadius: 16,
                    padding: '4px 14px',
                    marginBottom: 16,
                }}>
                    <span style={{
                        color: '#818CF8',
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: THEME.fonts.body,
                    }}>
                        {displayTier} Plan
                    </span>
                </div>

                <p style={{
                    color: `${THEME.colors.textPrimary}B3`,
                    fontSize: 14,
                    lineHeight: 1.6,
                    textAlign: 'center',
                    margin: '0 0 40px',
                    padding: '0 8px',
                    fontFamily: THEME.fonts.body,
                }}>
                    Your institution's Resido subscription has expired or is not active. Please contact your hostel administrator or mess secretary to renew the subscription.
                </p>

                {/* Info Card */}
                <div style={{
                    width: '100%',
                    background: 'rgba(30, 41, 59, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 24,
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    padding: 24,
                    marginBottom: 24,
                }}>
                    <p style={{
                        color: `${THEME.colors.textPrimary}99`,
                        fontSize: 16,
                        textAlign: 'center',
                        margin: '0 0 16px',
                        fontFamily: THEME.fonts.body,
                    }}>
                        What to do
                    </p>

                    {[
                        'Contact your hostel administrator',
                        'Ask them to renew the subscription',
                        'Access will be restored automatically',
                        'No action needed on your end',
                    ].map((step, i) => (
                        <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '8px 0',
                        }}>
                            <div style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: `${THEME.colors.tealAccent}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 13,
                                fontWeight: 700,
                                color: THEME.colors.tealAccent,
                                fontFamily: THEME.fonts.body,
                                flexShrink: 0,
                            }}>
                                {i + 1}
                            </div>
                            <span style={{
                                color: `${THEME.colors.textPrimary}CC`,
                                fontSize: 14,
                                fontFamily: THEME.fonts.body,
                            }}>
                                {step}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <p style={{
                    color: `${THEME.colors.textPrimary}80`,
                    fontSize: 12,
                    textAlign: 'center',
                    margin: '0 0 24px',
                    fontFamily: THEME.fonts.body,
                }}>
                    Managed by your institution • Resido Platform
                </p>
            </div>

            {/* CSS Animations */}
            <style>{`
                @keyframes glow-pulse {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 0.6; transform: scale(1.05); }
                }
                @keyframes icon-pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }
            `}</style>
        </div>
    );
}
